import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";
import LoadingCircle from "../../utils/LoadingCircle";
import GolfRenewalNoticeDialog from "../components/GolfRenewalNoticeDialog";
import "./ReservationEnd.css";

interface PropsType {
  userState: userState;
}

type FacilityFilter = "HEALTH" | "GOLF" | "STUDY" | "LOCKER";

interface ReservationDetails {
  userName: string;
  userId: string;
  inputDt: string;
  reservDt: string;
  reservOption: string;
  deviceNum: string;
}

interface ReservationInfo {
  sk: string;
  pk: string;
  reservations: Record<string, ReservationDetails>;
}

interface Reservation {
  sk: string;
  pk: string;
  communityPrice?: number | string;
  reservInfo: ReservationInfo;
  state: string;
}

interface FacilityConfig {
  id: FacilityFilter;
  label: string;
  defaultPrice: number;
  cancelConfirm: string;
  cancelSuccess: string;
  cancelFailure: string;
}

interface ReceiptEntry {
  facility: FacilityConfig;
  reservation: Reservation;
  details: ReservationDetails;
  detailKey: string;
  price: number;
}

const FACILITIES: readonly FacilityConfig[] = [
  {
    id: "HEALTH",
    label: "헬스장",
    defaultPrice: 5000,
    cancelConfirm: "신청을 취소하시겠습니까?\n[주의] 확인 후 사용료가 청구될 수 있습니다.",
    cancelSuccess: "신청이 취소되었습니다.",
    cancelFailure: "신청 취소에 실패하였습니다.\n다시 조회 후 진행해주세요.",
  },
  {
    id: "GOLF",
    label: "골프장 예약",
    defaultPrice: 20000,
    cancelConfirm: "신청을 취소하시겠습니까?\n[주의] 확인 후 사용료가 청구될 수 있습니다.",
    cancelSuccess: "신청이 취소되었습니다.",
    cancelFailure: "신청 취소에 실패하였습니다.\n다시 조회 후 진행해주세요.",
  },
  {
    id: "STUDY",
    label: "독서실",
    defaultPrice: 20000,
    cancelConfirm: "신청을 취소하시겠습니까?\n[주의] 확인 후 사용료가 청구될 수 있습니다.",
    cancelSuccess: "신청이 취소되었습니다.",
    cancelFailure: "신청 취소에 실패하였습니다.\n다시 조회 후 진행해주세요.",
  },
  {
    id: "LOCKER",
    label: "락커룸",
    defaultPrice: 5000,
    cancelConfirm: "락커 신청을 취소하시겠습니까?\n[주의] 확인 후 사용료가 청구될 수 있습니다.",
    cancelSuccess: "락커 신청이 취소되었습니다.",
    cancelFailure: "락커 신청 취소에 실패하였습니다.\n다시 조회 후 진행해주세요.",
  },
];

const EMPTY_RESERVATIONS: Record<FacilityFilter, Reservation[]> = {
  HEALTH: [],
  GOLF: [],
  STUDY: [],
  LOCKER: [],
};

const CANCEL_OPEN_DAYS = ["25", "26", "27", "28", "29", "30", "31"];
const mainApi = new HttpMainApi();

function isMonthlyReservation(reservation: Reservation, facility: FacilityFilter) {
  const keyParts = reservation.pk.split("/");
  return keyParts.length === 2 && /^\d{6}$/.test(keyParts[0]) && keyParts[1] === facility;
}

function getReservationPrice(facility: FacilityConfig, reservation: Reservation, details: ReservationDetails) {
  const storedPrice = Number(reservation.communityPrice);
  if (Number.isFinite(storedPrice) && storedPrice > 0) return storedPrice;

  if (facility.id === "LOCKER") {
    const lockerType = `${reservation.reservInfo.sk} ${details.deviceNum}`;
    return lockerType.includes("헬스") ? 3000 : 5000;
  }

  return facility.defaultPrice;
}

function formatPrice(price: number) {
  return `${new Intl.NumberFormat("ko-KR").format(price)}원`;
}

function formatReservationMonth(reservationMonth: string) {
  return `${reservationMonth.substring(0, 4)}년 ${reservationMonth.substring(4, 6)}월`;
}

function formatCancellationDeadline(reservationMonth: string) {
  const year = Number(reservationMonth.substring(0, 4));
  const month = Number(reservationMonth.substring(4, 6));
  const lastDayOfPreviousMonth = new Date(year, month - 1, 0);

  return `${lastDayOfPreviousMonth.getFullYear()}년 ${String(lastDayOfPreviousMonth.getMonth() + 1).padStart(
    2,
    "0"
  )}월 ${String(lastDayOfPreviousMonth.getDate()).padStart(2, "0")}일까지`;
}

function getReceiptDescription(facility: FacilityFilter, details: ReservationDetails) {
  if (facility === "HEALTH" || facility === "GOLF") return "월 이용권 · 05:00 ~ 23:00";

  return [details.deviceNum, details.reservOption].filter(Boolean).join(" · ");
}

const ReservationEnd = ({ userState }: PropsType) => {
  const [reservations, setReservations] = useState<Record<FacilityFilter, Reservation[]>>(EMPTY_RESERVATIONS);
  const [isLoading, setIsLoading] = useState(false);

  const loadReservations = useCallback(async () => {
    setIsLoading(true);
    const { formatmonthpk } = getFormattedDateTime();

    try {
      const results = await Promise.all(
        FACILITIES.map(async (facility) => {
          const response = await mainApi.get_month_reserv_data({
            pk: userState.userId,
            sk: formatmonthpk,
            reservState: "신청",
            reservFilter: facility.id,
          });

          if (response.code === "200") {
            const monthlyReservations = ((response.response.result ?? []) as Reservation[]).filter((reservation) =>
              isMonthlyReservation(reservation, facility.id)
            );
            return [facility.id, monthlyReservations] as const;
          }
          if (response.code === "400") return [facility.id, []] as const;

          alert(
            `알수 없는 에러가 발생했습니다.\n- CODE : ${response.code}\n- message : ${response.response.error_msg}`
          );
          return [facility.id, []] as const;
        })
      );

      setReservations(
        results.reduce<Record<FacilityFilter, Reservation[]>>(
          (nextReservations, [facility, facilityReservations]) => ({
            ...nextReservations,
            [facility]: facilityReservations,
          }),
          { ...EMPTY_RESERVATIONS }
        )
      );
    } catch (error) {
      alert(`예약 내역을 불러오지 못했습니다.\n${error}`);
    } finally {
      setIsLoading(false);
    }
  }, [userState.userId]);

  useEffect(() => {
    void loadReservations();
  }, [loadReservations]);

  const receiptEntries = useMemo(
    () =>
      FACILITIES.flatMap((facility) =>
        reservations[facility.id].flatMap((reservation) =>
          Object.entries(reservation.reservInfo.reservations).map(([detailKey, details]) => ({
            facility,
            reservation,
            details,
            detailKey,
            price: getReservationPrice(facility, reservation, details),
          }))
        )
      ),
    [reservations]
  );

  const receiptGroups = useMemo(() => {
    const entriesByMonth = receiptEntries.reduce<Record<string, ReceiptEntry[]>>((groups, entry) => {
      const reservationMonth = entry.reservation.pk.substring(0, 6);
      return {
        ...groups,
        [reservationMonth]: [...(groups[reservationMonth] ?? []), entry],
      };
    }, {});

    return Object.entries(entriesByMonth)
      .sort(([leftMonth], [rightMonth]) => rightMonth.localeCompare(leftMonth))
      .map(([reservationMonth, entries]) => ({
        reservationMonth,
        label: formatReservationMonth(reservationMonth),
        entries,
        totalPrice: entries.reduce((total, entry) => total + entry.price, 0),
      }));
  }, [receiptEntries]);

  const totalPrice = receiptEntries.reduce((total, receipt) => total + receipt.price, 0);

  const cancelReservation = async (facility: FacilityConfig, reservation: Reservation) => {
    if (!window.confirm(facility.cancelConfirm)) return;

    const { formattedDateTime } = getFormattedDateTime();
    const response = await mainApi.put_cancel_my_reservation({
      pk: reservation.pk,
      sk: reservation.sk,
      inputDt: formattedDateTime,
    });

    if (response.code === "200") {
      alert(facility.cancelSuccess);
      await loadReservations();
      return;
    }
    if (response.code === "400") {
      alert(facility.cancelFailure);
      return;
    }
    alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${response.code}\n- message : ${response.response.error_msg}`);
  };

  const renderReceipt = ({ facility, reservation, details, detailKey, price }: ReceiptEntry) => {
    const { formatmonthpk, formatday } = getFormattedDateTime();
    const reservationMonth = reservation.pk.substring(0, 6);
    const canCancel = reservationMonth !== formatmonthpk && CANCEL_OPEN_DAYS.includes(formatday);

    return (
      <Box
        component="article"
        aria-label={`${facility.label} ${details.reservDt} 신청 내역`}
        className="hp-reservation-receipt"
        key={`${reservation.pk}-${reservation.sk}-${detailKey}`}
      >
        <Box className="hp-reservation-receipt__main">
          <Box className="hp-reservation-receipt__content">
            <span className="hp-reservation-receipt__facility">{facility.label}</span>
            <Typography component="h3" className="hp-reservation-receipt__title">
              {details.reservDt}
            </Typography>
            <span className="hp-reservation-receipt__description">{getReceiptDescription(facility.id, details)}</span>
          </Box>

          <Box className="hp-reservation-receipt__amount">
            <strong>{formatPrice(price)}</strong>
            <span className="hp-reservation-receipt__status">
              <CheckCircleRoundedIcon aria-hidden="true" />
              신청 완료
            </span>
          </Box>
        </Box>

        <Box className="hp-reservation-receipt__footer">
          <Box className="hp-reservation-receipt__dates">
            <span>{details.inputDt} 신청</span>
            <span className={canCancel ? "" : "is-expired"}>
              {canCancel ? `취소 가능 ${formatCancellationDeadline(reservationMonth)}` : "취소 가능 기간이 지났습니다."}
            </span>
          </Box>
          <Button
            className="hp-reservation-receipt__cancel"
            disabled={!canCancel}
            variant="text"
            onClick={() => void cancelReservation(facility, reservation)}
          >
            {canCancel ? "신청취소" : "취소기간 아님"}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <main className="hpoint-renewal hp-reservation-end-page">
        <header className="hp-reservation-end-header">
          <Typography component="h1" className="hp-page-title">
            신청 내역
          </Typography>
          <Typography className="hp-page-description">헬스장·골프장·독서실·락커룸 월 신청 내역입니다.</Typography>
        </header>

        <section className="hp-reservation-end-summary" aria-label="월 이용 신청 요약">
          <Box className="hp-reservation-end-summary__icon" aria-hidden="true">
            <EventNoteRoundedIcon />
          </Box>
          <Box>
            <span>월 이용 신청</span>
            <strong>
              총 {receiptEntries.length}건 · {formatPrice(totalPrice)}
            </strong>
          </Box>
        </section>

        <Box className="hp-reservation-end-list" aria-live="polite">
          {!isLoading && receiptEntries.length === 0 ? (
            <section className="hp-reservation-end-empty" aria-label="예약 내역 없음">
              <InfoOutlinedIcon aria-hidden="true" />
              <Typography component="h2">신청된 월 이용 내역이 없습니다.</Typography>
              <Typography>커뮤니티 예약에서 신청한 네 가지 월 이용 내역이 여기에 표시됩니다.</Typography>
            </section>
          ) : (
            <Box className="hp-reservation-end-month-groups">
              {receiptGroups.map(({ reservationMonth, label, entries, totalPrice: monthTotalPrice }) => (
                <section className="hp-reservation-end-month-group" aria-label={`${label} 월 이용 신청 내역`} key={reservationMonth}>
                  <header className="hp-reservation-end-month-group__header">
                    <Typography component="h2">{label}</Typography>
                    <span>
                      {entries.length}건 · {formatPrice(monthTotalPrice)}
                    </span>
                  </header>
                  <section className="hp-reservation-receipt-sheet" aria-label={`${label} 신청 영수증`}>
                    {entries.map(renderReceipt)}
                  </section>
                </section>
              ))}
            </Box>
          )}
        </Box>

        <aside className="hp-reservation-end-notice">
          <InfoOutlinedIcon aria-hidden="true" />
          <Box>
            <Typography>이용료는 골프장·독서실 20,000원, 헬스장 5,000원, 락커룸은 골프 5,000원·헬스 3,000원입니다.</Typography>
            <Typography>다음 달 신청 취소는 매월 25일부터 말일까지 가능합니다. 버튼이 비활성화되면 관리사무소에 문의해 주세요.</Typography>
          </Box>
        </aside>

      <LoadingCircle loading={isLoading} />
      <GolfRenewalNoticeDialog />
    </main>
  );
};

export default ReservationEnd;
